import CtaButton from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import FillImage from '@/components/FillImage';
import RightCheveron from '@/components/Icons/RightCheveron';
import ImageMarqueeCarousel from '@/components/ImageMarqueeCarousel';
import PageLayout from '@/components/PageLayout';
import Footer from '@/components/PageLayout/Footer';
import { EmptyLinkNavBar } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useTimeout from '@/hooks/useTimeout';
import {
  CLIENT_DISCOVER_INTERACT_URL,
  CLIENT_DISCOVER_TUTORIAL_URL,
  CLIENT_INDEX_URL,
  defaultImageMarqueeCarouselImages,
} from '@/utils/constants';
import { DISCOVER_TUTORIAL } from '@/utils/localStorageKeys';
import { useRouter } from 'next/router';
import { useState } from 'react';

export default function DiscoverSplashPage() {
  const router = useRouter();
  const [darkMode, setDarkMode] = useState(false);

  useTimeout(() => setDarkMode(true), 2000);

  const routeUser = () => {
    const isTutorialComplete = window.localStorage.getItem(DISCOVER_TUTORIAL);
    if (!isTutorialComplete) router.push(CLIENT_DISCOVER_TUTORIAL_URL);
    else return router.push(CLIENT_DISCOVER_INTERACT_URL);
  };

  return (
    <div className={`${darkMode ? 'dark' : ''}`}>
      <div className="overflow-hidden bg-white dark:bg-black dark:text-white transition-dark">
        <PageLayout>
          <EmptyLinkNavBar href={CLIENT_INDEX_URL} />
          <TwelveColGrid py="lgt:pt-8.0 pt-16 pb-[4rem] lgt:pb-32">
            <div className="col-span-12 text-center lgt:col-span-4 lgt:col-start-5">
              <span className="block copy-xl mb-1.6">DISCOVER</span>
              <span className="text-black-300 text-body">
                Explore artwork by artist, medium, theme and color to discover
                what you love to collect.
              </span>
            </div>
          </TwelveColGrid>
          <ImageMarqueeCarousel
            imgUrlArray={defaultImageMarqueeCarouselImages}
          />

          <TwelveColGrid py="pt-[4rem] lgt:pt-32 pb-48">
            <div className="flex justify-center col-span-12 ">
              <CtaButton
                onClick={routeUser}
                color="text-white dark:text-black transition-dark"
                background="bg-black dark:bg-white"
              >
                <CtaText>Start</CtaText>
                <RightCheveron />
              </CtaButton>
            </div>
          </TwelveColGrid>

          {/* TODO, MAKE REUSABLE COMPONENT FOR EACH SECTION */}
          <DiscoverExplanationSection
            title={'SWIPE AWAY'}
            desc={
              "Swipe right if you like an artwork, swipe left if it doesn't appeal to you. Discover what you love."
            }
            titleCols={'col-span-12 lgt:col-span-4'}
          >
            <div className="lgt:hidden flex justify-center col-span-12 mt-16 mb-3.2">
              <FillImage
                height="h-[37.6rem]"
                width="w-[17.4rem]"
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1658153715/Discover/swipe_away_mobile_gapfkf.png'
                }
              />
            </div>
            <div className="justify-center hidden col-span-8 lgt:flex mb-6.4 lgt:mt-3.2">
              <FillImage
                height="h-[42.4rem]"
                width="w-[74.4rem]"
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1658153715/Discover/swipe_away_desktop_a1ucws.png'
                }
              />
            </div>
          </DiscoverExplanationSection>
          <DiscoverExplanationSection
            title={'KNOW THE DETAILS'}
            desc={
              'Learn more and get insights about the artwork you love by tapping on ( ... )'
            }
            titleCols={
              'col-span-12 lgt:col-span-4 lgt:col-start-7 lgt:row-start-2'
            }
          >
            <div className="mt-28 justify-center col-span-12 lgt:col-span-4 lgt:row-start-2 lgt:col-start-2 lgt:mt-3.2 flex mb-3.2 lgt:mb-[11.2rem]">
              <FillImage
                height="h-[34.4rem]"
                width="w-[24rem]"
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1662048225/Discover/Tutorial/tutorial_slide_2_v3y2xa.png'
                }
              />
            </div>
          </DiscoverExplanationSection>
          <DiscoverExplanationSection
            title={'GET UNLOCKS'}
            desc={
              'Unlocks provide access to acquire rare artworks and exclusive releases as you discover what you love'
            }
            titleCols={'col-span-12 lgt:col-span-4'}
          >
            <div className="relative mt-24 justify-center col-span-12 lgt:mt-3.2 flex mb-80 lgt:mb-72 lgt:col-span-6 lgt:col-start-7">
              <div
                style={{
                  background:
                    'radial-gradient(52.92% 100% at 50% 0%, #D070A4 0%, rgba(16, 16, 16, 0) 100%), #0F0F0F',
                }}
                className="absolute -top-[19.5rem] lgt:-top-[3.2rem] w-screen lgt:w-full h-[56rem]  lgt:h-[48.8rem] left-1/2 -translate-x-1/2 "
              ></div>
              <FillImage
                height="h-[40rem]"
                width="w-[34.4rem]"
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1660770783/Discover/get_unlocks_desktop_te8fn1.png'
                }
              />
            </div>
          </DiscoverExplanationSection>
          <Footer />
        </PageLayout>
      </div>
    </div>
  );
}

function DiscoverExplanationSection({ title, desc, titleCols, children }: any) {
  return (
    <TwelveColGrid addClassStyles="relative" gap="gap-x-0.8 lgt:gap-x-2.4">
      <div className="relative z-10 col-span-12 row-start-1 border-t border-black-200"></div>
      <div
        className={`relative z-10 text-center lgt:text-left col-span-12 lgt:col-span-4 mt-2.4 ${titleCols}`}
      >
        <h5 className="uppercase copy-l mb-1.6">{title}</h5>
        <p className="text-body h-7.2">{desc}</p>
      </div>
      {children}
    </TwelveColGrid>
  );
}
